import { useTranslation } from 'react-i18next';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { readPostalAddress } from '@/lib/referral/xref01Validation';

// Spor A block 3: the clinic as sender of an XDIS18 or XDIS91 answer — the same fields the
// REF01 answers use (connection EAN, SOR identifier, clinic name/address/phone, speciality).

function filled(entries: Record<string, string | null | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

export function useXdisSender(clinicId: string) {
  const { t } = useTranslation();
  const clinic = useClinic(clinicId);
  const connection = useClinicCommunicateConnection(clinicId);
  const sor = useSorBySorId(connection.data?.sor_id ?? null);
  const medicalSpecialityCode = readClinicMedicalSpecialityCode(connection.data?.medical_speciality_code);
  const gap = !connection.data?.ean
    ? t('referrals.revisit_sender_missing_ean', 'Klinikken har ingen MedCom-forbindelse med lokationsnummer.')
    : !sor.data?.identifier || !sor.data.identifierCode
      ? t('referrals.revisit_sender_missing_sor', 'Klinikkens SOR-enhed mangler identifikator.')
      : !medicalSpecialityCode
        ? t('referrals.revisit_sender_missing_speciality', 'Klinikkens speciale (MedicalSpecialityCode) er ikke konfigureret.')
        : !clinic.data?.name
          ? t('patients.xdis.sender_loading', 'Klinikkens oplysninger er ikke indlæst endnu.')
          : null;
  const sender =
    gap || !clinic.data || !connection.data?.ean || !sor.data?.identifier || !sor.data.identifierCode || !medicalSpecialityCode
      ? null
      : (() => {
          const address = readPostalAddress(clinic.data.address);
          return filled({
            ean: connection.data.ean,
            identifier: sor.data.identifier,
            identifierCode: sor.data.identifierCode,
            organisationName: clinic.data.name,
            streetName: address.street,
            postCodeIdentifier: address.postCode,
            districtName: address.district,
            telephoneSubscriberIdentifier: clinic.data.phone,
            medicalSpecialityCode,
          });
        })();
  return { sender, gap };
}
